import React, { useEffect, useRef } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import {
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Button,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  Typography,
  CircularProgress,
  TextField,
  Box,
  Avatar,
  Card,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Swal from "sweetalert2";

// Child sections (still using refs for now)
import BundleOfferSection from "./BundleOfferSection";
import XYOfferSection from "./XYOfferSection";
import CouponSection from "./CouponSection";
import PurchaseRewardSection from "./PurchaseRewardSection";
import LoyaltySection from "./LoyaltySection";
import ConstraintsSection from "./ConstraintsSection";
import ExclusionsSection from "./ExclusionsSection";

// RTK Query hooks
import {
  useGetOfferQuery,
  useGetOfferTypesQuery,

  useCreateOfferMutation,
  useUpdateOfferMutation,
} from "../../redux/Slices/offersApi";

// Zod schema
import { offerSchema, getDefaultOfferValues } from "./schemas/offerSchema";
import { useGetAllStoresQuery } from "../../redux/Slices/storeApi";

const targetAudienceOptions = [
  { value: "all", label: "الكل" },
  { value: "new_users", label: "المستخدمين الجدد فقط" },
  { value: "logged_in_only", label: "المسجلين فقط" },
];

export default function OfferDataSection() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  // Refs for child sections (keeping existing pattern for compatibility)
  const bundleOfferRef = useRef();
  const xyOfferRef = useRef();
  const couponRef = useRef();
  const purchaseRewardRef = useRef();
  const loyaltyRef = useRef();

  // RTK Query hooks
  const { data: offerData, isLoading: isLoadingOffer } = useGetOfferQuery(id, {
    skip: !id,
  });
  const { data: offerTypesData, isLoading: isLoadingOfferTypes } =
    useGetOfferTypesQuery();

  const { data: storesData, isLoading: isLoadingStores } = useGetAllStoresQuery();
  console.log("storesData", storesData);
  const [createOffer, { isLoading: isCreating }] = useCreateOfferMutation();
  const [updateOffer, { isLoading: isUpdating }] = useUpdateOfferMutation();

  // React Hook Form setup (Manual validation in onSubmit to handle ref-based data)
  const methods = useForm({
    defaultValues: getDefaultOfferValues(),
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  // Watch offer type for conditional rendering
  const offerType = watch("promotion_type_id");
  const storeId = watch("store_id");
  const constraints = watch("promotion_constraints");
  const exclusions = watch("promotion_exclusions");

  // Snackbar state
  const [showTypeChangeError, setShowTypeChangeError] = React.useState(false);

  // Extract data from RTK Query responses
  const offerTypes = offerTypesData?.data?.promotion_types || [];
  const stores = storesData?.data || [];

  // Populate form when editing
  useEffect(() => {
    if (offerData?.data && isEditMode) {
      const offer = offerData.data;
      reset({
        name: offer.name || "",
        start_date: offer.start_date ? offer.start_date.split("T")[0] : getDefaultOfferValues().start_date,
        end_date: offer.end_date ? offer.end_date.split("T")[0] : "",
        promotion_type_id: offer.promotion_type_id || 1,
        target_audience: offer.target_audience || "all",
        is_super_offer: Boolean(offer.is_super_offer),
        is_active: offer.is_active ? "1" : "0",
        store_id: offer.store_id || null,
        promotion_constraints: offer.promotion_constraints || [],
        promotion_exclusions: offer.promotion_exclusions || [],
      });

      // Set data for child sections based on offer type
      if (offer.promotion_type?.name === "bundles" && bundleOfferRef.current) {
        setTimeout(() => {
          bundleOfferRef.current?.setBundleDataFromApi(
            offer.bundle,
            offer.gift_types,
            offer.free_products
          );
        }, 100);
      } else if (offer.promotion_type?.name === "buy_x_get_y_offers" && xyOfferRef.current) {
        setTimeout(() => {
          xyOfferRef.current?.setXYDataFromApi(
            offer.buy_x_get_y_products,
            offer.free_products,
            offer.gift_types
          );
        }, 100);
      } else if (offer.promotion_type?.name === "coupons" && couponRef.current) {
        setTimeout(() => {
          couponRef.current?.setCouponDataFromApi(
            offer.coupon,
            offer.gift_types,
            offer.free_products
          );
        }, 100);
      } else if (offer.promotion_type?.name === "purchase_rewards" && purchaseRewardRef.current) {
        setTimeout(() => {
          purchaseRewardRef.current?.setPurchaseRewardDataFromApi(
            offer.purchase_rewards,
            offer.gift_types,
            offer.free_products
          );
        }, 100);
      } else if (offer.promotion_type?.name === "loyalty_rewards" && loyaltyRef.current) {
        setTimeout(() => {
          loyaltyRef.current?.setLoyaltyDataFromApi(
            offer.loyalty_reward,
            offer.promotion_loyalty_tiers,
            offer.gift_types
          );
        }, 100);
      }
    }
  }, [offerData, isEditMode, reset]);

  // Handle offer type change
  const handleOfferTypeChange = (event) => {
    if (isEditMode) {
      setShowTypeChangeError(true);
      return;
    }
    setValue("promotion_type_id", Number(event.target.value));
  };

  // Get available gift types for selected offer type
  const getAvailableGiftTypes = () => {
    const selectedOfferType = offerTypes.find((type) => type.id === offerType);
    return selectedOfferType?.gift_types || [];
  };

  // Form submission
  const onSubmit = async (formData) => {
    // 1. Clear previous errors
    clearErrors();

    // 2. Prepare combined data for validation
    let combinedData = {
      ...formData,
      promotion_constraints: constraints,
      promotion_exclusions: exclusions.map((ex) => ({
        excluded_promotion_id: ex.id?.toString() || ex.excluded_promotion_id,
      })),
    };

    console.log("Submitting Offer Data - Initial:", combinedData);

    // 3. Collect data from child sections based on offer type
    if (offerType === 1 && bundleOfferRef.current) {
      const bundleData = bundleOfferRef.current.getBundleData();
      const giftData = bundleOfferRef.current.getGiftData();
      combinedData = { ...combinedData, ...bundleData, rewards: giftData.rewards };
    } else if (offerType === 2 && xyOfferRef.current) {
      const xyData = xyOfferRef.current.getXYData();
      const giftData = xyOfferRef.current.getGiftData();
      combinedData = {
        ...combinedData,
        buy_x_get_y_offer: {
          buy_x_get_y_products: xyData.xy_products
        },
        rewards: giftData.rewards,
      };
    } else if (offerType === 3 && couponRef.current) {
      const couponData = couponRef.current.getCouponData();
      const giftData = couponRef.current.getGiftData();
      combinedData = { ...combinedData, ...couponData, rewards: giftData.rewards };
    } else if (offerType === 4 && purchaseRewardRef.current) {
      const purchaseRewardData = purchaseRewardRef.current.getPurchaseRewardData();
      const giftData = purchaseRewardRef.current.getGiftData();
      combinedData = { ...combinedData, ...purchaseRewardData, rewards: giftData.rewards };
    } else if (offerType === 6 && loyaltyRef.current) {
      const loyaltyData = loyaltyRef.current.getLoyaltyData();
      if (loyaltyData.rewards) {
        combinedData.rewards = loyaltyData.rewards;
      }
      if (loyaltyData.loyalty_offer) {
        combinedData.loyalty_reward = {
          name: loyaltyData.loyalty_offer.name,
          points_required: loyaltyData.loyalty_offer.points_required,
          coupon: loyaltyData.loyalty_offer.coupon || "",
        };
      }
      if (loyaltyData.loyalty_offer?.loyalty_tier_ids?.length) {
        combinedData.promotion_loyalty_tiers = loyaltyData.loyalty_offer.loyalty_tier_ids.map(
          (tierId) => ({ loyalty_tier_id: tierId.toString() })
        );
      }
    }

    // 4. Ensure numeric fields from refs are actually numbers
    if (combinedData.purchase_rewards) {
      combinedData.purchase_rewards = combinedData.purchase_rewards.map((pr) => ({
        ...pr,
        amount: Number(pr.amount || 0),
      }));
    }
    if (combinedData.rewards?.promotion_gift_types) {
      combinedData.rewards.promotion_gift_types = combinedData.rewards.promotion_gift_types.map(
        (gt) => ({
          ...gt,
          value: gt.value ? Number(gt.value) : undefined,
          percent: gt.percent ? Number(gt.percent) : undefined,
        })
      );
    }

    // Explicitly cast numeric fields in bundle and xy products
    if (combinedData.bundle) {

      combinedData.bundle.min_selections = Number(combinedData.bundle.min_selections || 1);
      combinedData.bundle.max_selections = Number(combinedData.bundle.max_selections || 1);

      if (combinedData.bundle.bundle_products) {
        combinedData.bundle.bundle_products = combinedData.bundle.bundle_products.map((p) => ({
          ...p,
          product_id: Number(p.product_id),
          is_required: Number(p.is_required || 0),
          default_quantity: Number(p.default_quantity || 1),
          min_quantity: Number(p.min_quantity || 1),
          max_quantity: Number(p.max_quantity || 1),
        }));
      }
    }
    if (combinedData.buy_x_get_y_offer?.buy_x_get_y_products) {
      combinedData.buy_x_get_y_offer.buy_x_get_y_products = combinedData.buy_x_get_y_offer.buy_x_get_y_products.map((p) => ({
        ...p,
        product_id: Number(p.product_id),
        min_quantity: Number(p.min_quantity || 1),
        max_quantity: p.max_quantity ? Number(p.max_quantity) : null,
      }));
    }

    // CLEANUP: Remove irrelevant fields based on offerType to prevent Zod validation errors
    if (offerType !== 1) delete combinedData.bundle;
    if (offerType !== 2) delete combinedData.buy_x_get_y_offer;
    if (offerType !== 3) delete combinedData.coupon;
    if (offerType !== 4) delete combinedData.purchase_rewards;
    if (offerType !== 6) {
      delete combinedData.loyalty_reward;
      delete combinedData.promotion_loyalty_tiers;
    }

    // Map constraints to match API expectations (type, value, start_time, end_time)
    combinedData.promotion_constraints = constraints?.map((c) => {
      if (c.type === "time_limit") {
        return {
          type: c.type,
          start_time: c.start_time,
          end_time: c.end_time,
        };
      }
      return {
        type: c.type,
        value: c.value?.toString() || "",
      };
    }) || [];

    // Map promotion_exclusions is already done above, but ensure correctness

    console.log("Submitting Offer Data - Cleaned:", combinedData);

    // 5. Perform manual validation with Zod
    const validationResult = offerSchema.safeParse(combinedData);

    if (!validationResult.success) {
      console.error("Zod Validation Errors:", validationResult.error.issues);

      // Map Zod errors back to React Hook Form
      validationResult.error.issues.forEach((issue) => {
        const path = issue.path.join(".");
        setError(path, {
          type: "manual",
          message: issue.message,
        });
      });

      // Show alert for validation errors
      // Show alert for validation errors with details
      Swal.fire({
        title: "خطأ في البيانات",
        html: `<div style="text-align: right; direction: rtl;">
                 <p>يرجى التحقق من الحقول التالية:</p>
                 <ul style="list-style-type: disc; margin-right: 20px;">
                   ${validationResult.error.issues
            .map((issue) => `<li>${issue.message}</li>`)
            .join("")}
                 </ul>
               </div>`,
        icon: "warning",
        confirmButtonText: "حسناً",
      });
      return;
    }

    // 5. Finalize data for API submission (mapping/formatting)
    const finalData = {
      ...combinedData,
      is_super_offer: combinedData.is_super_offer ? 1 : 0,
      description: "عرض جديد",
    };

    console.log("Submitting Final Data to API:", finalData);

    try {
      // Remove blocking loader to show button loading state
      // Swal.fire({ ... });

      let result;
      if (isEditMode) {
        result = await updateOffer({ id, ...finalData }).unwrap();
      } else {
        result = await createOffer(finalData).unwrap();
      }

      console.log("API Result:", result);

      if (result.status) {
        Swal.fire({
          title: "تم الحفظ بنجاح",
          text: "تم حفظ العرض بنجاح",
          icon: "success",
          confirmButtonText: "حسناً",
        }).then(() => {
          if (!isEditMode) {
            reset(getDefaultOfferValues());
            bundleOfferRef.current?.reset?.();
            xyOfferRef.current?.reset?.();
            couponRef.current?.reset?.();
            purchaseRewardRef.current?.reset?.();
            loyaltyRef.current?.reset?.();
          } else {
            navigate("/offers");
          }
        });
      } else {
        console.error("API Error Result:", result);
        Swal.fire({
          title: "خطأ",
          text: result.message || "حدث خطأ أثناء حفظ العرض",
          icon: "error",
          confirmButtonText: "حسناً",
        });
      }
    } catch (error) {
      console.error("API Request Error:", error);
      Swal.fire({
        title: "خطأ",
        text: error.data?.message || "حدث خطأ أثناء حفظ العرض",
        icon: "error",
        confirmButtonText: "حسناً",
      });
    }
  };

  // Loading state
  if (isLoadingOfferTypes || (isEditMode && isLoadingOffer)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px" py={6}>
        <CircularProgress size={60} sx={{ color: "#ca9937" }} />
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <Card >
        <MDBox
          display="flex"
          flexDirection="column"
          gap={3}
          p={3}
          boxShadow={3}
          borderRadius="16px"
          bgcolor="#fff"
          mb={4}
          sx={{ width: "90%", margin: "0 auto" }}
        >
          <MDTypography variant="h5" fontWeight="bold" mb={2}>
            بيانات العرض
          </MDTypography>

          {/* Title, Target Audience, Store */}
          <MDBox display="flex" gap={2} alignItems="center">
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <MDInput
                  {...field}
                  label="عنوان العرض"
                  sx={{ flex: 2 }}
                  inputProps={{ dir: "rtl" }}
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                />
              )}
            />

            <Controller
              name="target_audience"
              control={control}
              render={({ field }) => (
                <FormControl sx={{ flex: 2 }}>
                  <InputLabel id="target-audience-label">الجمهور المستهدف</InputLabel>
                  <Select
                    {...field}
                    labelId="target-audience-label"
                    label="الجمهور المستهدف"
                    inputProps={{ dir: "rtl" }}
                    sx={{ height: 45 }}
                  >
                    {targetAudienceOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              name="store_id"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={stores}
                  getOptionLabel={(option) => option.name_ar || ""}
                  value={stores.find((store) => store.id === field.value) || null}
                  onChange={(event, newValue) => {
                    field.onChange(newValue ? newValue.id : null);
                  }}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Avatar
                        src={
                          option.logo
                            ? `https://back.al-balad.sa/uploads/stores/logos/${option.logo}`
                            : null
                        }
                        alt={option.name_ar}
                        sx={{ width: 32, height: 32 }}
                      >
                        {!option.logo && option.name_ar?.charAt(0)}
                      </Avatar>
                      {option.name_ar}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="اختر المتجر (اختياري)"
                      sx={{ flex: 2 }}
                      InputProps={{
                        ...params.InputProps,
                        dir: "rtl",
                        endAdornment: (
                          <>
                            {isLoadingStores ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  loading={isLoadingStores}
                  sx={{ flex: 2 }}
                />
              )}
            />
          </MDBox>

          {errors.name && (
            <Typography color="error" fontSize={13}>
              {errors.name.message}
            </Typography>
          )}

          {/* Dates */}
          <MDBox display="flex" gap={2} flexDirection={{ xs: "column", md: "row" }}>
            <Controller
              name="start_date"
              control={control}
              render={({ field }) => (
                <MDInput
                  {...field}
                  label="تاريخ بداية العرض"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  inputProps={{ dir: "rtl" }}
                  error={Boolean(errors.start_date)}
                />
              )}
            />
            <Controller
              name="end_date"
              control={control}
              render={({ field }) => (
                <MDInput
                  {...field}
                  label="تاريخ نهاية العرض"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  inputProps={{ dir: "rtl" }}
                  error={Boolean(errors.end_date)}
                />
              )}
            />
          </MDBox>

          {(errors.start_date || errors.end_date) && (
            <Typography color="error" fontSize={13}>
              {errors.start_date?.message || errors.end_date?.message}
            </Typography>
          )}

          {/* Offer Type, Status, Super Offer */}
          <FormControl>
            <MDBox display="flex" alignItems="center" gap={3}>
              <FormLabel
                component="legend"
                sx={{ mb: 1, fontWeight: "bold", color: "black", minWidth: 80 }}
              >
                نوع العرض
              </FormLabel>

              <RadioGroup
                aria-label="offerType"
                name="offerType"
                value={offerType}
                onChange={handleOfferTypeChange}
                sx={{ gap: 1, flexDirection: "row" }}
              >
                {offerTypes.map((type) => (
                  <FormControlLabel
                    key={type.id}
                    value={type.id}
                    control={<Radio />}
                    label={type.name}
                    sx={{ mr: 0, justifyContent: "flex-end" }}
                  />
                ))}
              </RadioGroup>



              <Controller
                name="is_super_offer"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="عرض سوبر"
                    sx={{ mr: 0 }}
                  />
                )}
              />
            </MDBox>
          </FormControl>
          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <FormControl sx={{ minWidth: 140 }}>
                <InputLabel id="is-active-label">حالة العرض</InputLabel>
                <Select
                  {...field}
                  labelId="is-active-label"
                  label="حالة العرض"
                  inputProps={{ dir: "rtl" }}
                  sx={{ height: 45 }}
                >
                  <MenuItem value="1">مفعل</MenuItem>
                  <MenuItem value="0">غير مفعل</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </MDBox>

        {/* Offer Type Specific Sections */}
        {offerType === 1 && (
          <BundleOfferSection
            ref={bundleOfferRef}
            storeId={storeId}
            error={errors.bundle}
            rewardsError={errors.rewards}
            giftTypes={getAvailableGiftTypes()}
          />
        )}
        {offerType === 2 && (
          <XYOfferSection
            ref={xyOfferRef}
            storeId={storeId}
            error={errors.buy_x_get_y_offer}
            rewardsError={errors.rewards}
            giftTypes={getAvailableGiftTypes()}
          />
        )}
        {offerType === 3 && (
          <CouponSection
            ref={couponRef}
            storeId={storeId}
            error={errors.coupon}
            rewardsError={errors.rewards}
            giftTypes={getAvailableGiftTypes()}
          />
        )}
        {offerType === 4 && (
          <PurchaseRewardSection
            ref={purchaseRewardRef}
            storeId={storeId}
            error={errors.purchase_rewards}
            rewardsError={errors.rewards}
            giftTypes={getAvailableGiftTypes()}
          />
        )}
        {offerType === 6 && (
          <LoyaltySection
            ref={loyaltyRef}
            storeId={storeId}
            error={errors.loyalty_reward}
            errors={{
              loyaltyName: errors.loyalty_reward?.name,
              pointsRequired: errors.loyalty_reward?.points_required,
              selectedTiers: errors.promotion_loyalty_tiers,
              rewardsError: errors.rewards,
            }}
            giftTypes={getAvailableGiftTypes()}
          />
        )}

        {/* Constraints and Exclusions */}
        <ConstraintsSection
          constraints={constraints || []}
          setConstraints={(newConstraints) =>
            setValue("promotion_constraints", newConstraints)
          }
        />
        <ExclusionsSection
          exclusions={exclusions || []}
          setExclusions={(newExclusions) =>
            setValue("promotion_exclusions", newExclusions)
          }
        />

        {/* Submit Button */}
        <MDBox display="flex" justifyContent="start" mt={4}>
          <Button
            variant="contained"
            color="primary"
            sx={{ width: 200, color: "white !important", bgcolor: "primary.dark", "&:hover": { bgcolor: "primary.main" } }}
            onClick={handleSubmit(onSubmit)}
            disabled={isCreating || isUpdating}
          >
            {isCreating || isUpdating ? <CircularProgress size={24} color="inherit" /> : "حفظ"}
          </Button>
        </MDBox>

        {/* Snackbar for type change error */}
        <Snackbar
          open={showTypeChangeError}
          autoHideDuration={3000}
          onClose={() => setShowTypeChangeError(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="error" onClose={() => setShowTypeChangeError(false)}>
            لا يمكن تغيير نوع العرض عند التعديل
          </Alert>
        </Snackbar>
      </Card>
    </FormProvider>
  );
}
